import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Toolbar,
  Stack,
  Card,
  CardContent,
  InputAdornment,
  CssBaseline,
  Collapse,
  Divider,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  Email as EmailIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  Launch as LaunchIcon,
  Description as DocumentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import HomeHeader from '../../Components/HomeHeader';
import HomeFooter from '../../Components/HomeFooter';
import { lightTheme } from '../../Utils/baseTheme';
import client from '../../API';
import { replaceConsoleError } from '../../Utils/toastNotifications';
import EnhancedPayoutCard from '../../CreatorUser/CreatorPages/Dashboard/creatorcomponents/PayoutCard';

// Email validation regex
const PAYPAL_EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const DOMAIN_CORRECTIONS = {
  'gmai.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
};

const money = (n = 0) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const PayoutPage = () => {
  const navigate = useNavigate();
  const { payoutId } = useParams();
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [paypalEmail, setPaypalEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailWarning, setEmailWarning] = useState("");
  
  // RPA state
  const [rpaStatus, setRpaStatus] = useState({
    loading: true,
    hasSigned: false,
    error: null,
  });
  const [rpaTemplate, setRpaTemplate] = useState(null);
  const [rpaCheckboxChecked, setRpaCheckboxChecked] = useState(false);
  const [showRpaDetails, setShowRpaDetails] = useState(false);
  const [rpaProcessing, setRpaProcessing] = useState(false);
  const [stripeVerifyDialog, setStripeVerifyDialog] = useState({ open: false, url: null });

  // Claim state (for EnhancedPayoutCard)
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [claimingPayoutId, setClaimingPayoutId] = useState(null);

  // Status checks — both 'pending' (campaign payouts) and 'approved' (direct payouts) are claimable
  const canClaimPayout = payout
    && !payout.withdrawal_id
    && ["pending", "approved"].includes(payout.status?.toLowerCase());
  const isAlreadyClaimed = payout && ["confirmed", "paid", "completed"].includes(payout.status?.toLowerCase());
  const isCancelled = payout?.status === "cancelled";
  const needsRpaAgreement = !rpaStatus.hasSigned && !rpaCheckboxChecked;

  /* ───────── Email Validation ───────── */
  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      return { error: "Email address is required", warning: "" };
    }
    const trimmedEmail = email.trim().toLowerCase();
    
    if (trimmedEmail.length > 254) {
      return { error: "Email address is too long (max 254 characters)", warning: "" };
    }
    if (!PAYPAL_EMAIL_REGEX.test(trimmedEmail)) {
      return { error: "Please enter a valid PayPal email address", warning: "" };
    }
    
    const emailParts = trimmedEmail.split('@');
    if (emailParts.length !== 2) {
      return { error: "Invalid email format", warning: "" };
    }
    
    const [localPart, domain] = emailParts;
    if (localPart.length === 0) {
      return { error: "Email address cannot start with @", warning: "" };
    }
    if (localPart.length > 64) {
      return { error: "Email username is too long", warning: "" };
    }
    
    if (DOMAIN_CORRECTIONS[domain]) {
      const suggestion = `${localPart}@${DOMAIN_CORRECTIONS[domain]}`;
      return { error: "", warning: `Did you mean ${suggestion}?` };
    }
    
    const invalidDomains = ['example.com', 'test.com', 'domain.com', 'email.com'];
    if (invalidDomains.includes(domain)) {
      return { error: "Please enter a real email address", warning: "" };
    }
    
    if (localPart.includes('..') || localPart.startsWith('.') || localPart.endsWith('.')) {
      return { error: "Invalid email format", warning: "" };
    }
    
    return { error: "", warning: "" };
  };

  /* ───────── RPA Status Check ───────── */
  const checkRpaStatus = async (creatorUsername) => {
    if (!creatorUsername) {
      console.error('❌ Cannot check RPA status: creator_username is required');
      setRpaStatus({
        loading: false,
        hasSigned: false,
        error: "Creator username is required",
      });
      return;
    }

    try {
      setRpaStatus(prev => ({ ...prev, loading: true }));
      console.log('🔍 Checking RPA status for creator:', creatorUsername);
      
      const response = await client.contracts.blitzpayRpa.checkStatus(creatorUsername);
      console.log('✅ RPA status response:', response);
      
      setRpaStatus({
        loading: false,
        hasSigned: response.has_signed || false,
        error: null,
      });

      if (response.has_signed) {
        setRpaCheckboxChecked(true);
      }

      if (!response.has_signed) {
        try {
          const templateResponse = await client.contracts.blitzpayRpa.getTemplate();
          setRpaTemplate(templateResponse.template);
        } catch (templateErr) {
          replaceConsoleError("Error fetching RPA template", templateErr);
        }
      }
    } catch (err) {
      replaceConsoleError("Error checking RPA status", err);
      setRpaStatus({
        loading: false,
        hasSigned: false,
        error: "Unable to verify agreement status",
      });
      
      try {
        const templateResponse = await client.contracts.blitzpayRpa.getTemplate();
        setRpaTemplate(templateResponse.template);
      } catch (templateErr) {
        // Silent fail
      }
    }
  };

  /* ───────── RPA Agreement Handler ───────── */
  const handleRpaAgreement = async () => {
    const creatorUsername = payout?.creator_id;
    
    if (!creatorUsername) {
      setSnack({
        open: true,
        msg: "Creator information is missing. Please contact support.",
        sev: "error"
      });
      return;
    }

    if (!paypalEmail || !paypalEmail.trim()) {
      setSnack({
        open: true,
        msg: "Please enter your PayPal email first",
        sev: "warning"
      });
      return;
    }

    const { error } = validateEmail(paypalEmail);
    if (error) {
      setSnack({
        open: true,
        msg: "Please enter a valid email address first",
        sev: "warning"
      });
      return;
    }

    if (!rpaCheckboxChecked) {
      setSnack({
        open: true,
        msg: "Please check the agreement checkbox to proceed",
        sev: "warning"
      });
      return;
    }

    setRpaProcessing(true);
    try {
      const signatureData = {
        creator_username: creatorUsername,
        signer_email: paypalEmail.trim().toLowerCase(),
        signer_name: payout?.creator_display_name || creatorUsername,
        payout_id: payoutId,
      };
      
      console.log('📝 Signing RPA with data:', signatureData);
      await client.contracts.blitzpayRpa.createAndSign(signatureData);

      setRpaStatus(prev => ({ ...prev, hasSigned: true }));
      setSnack({
        open: true,
        msg: "Agreement confirmed successfully!",
        sev: "success",
      });
    } catch (err) {
      replaceConsoleError("Error signing RPA", err);
      
      let errorMsg = "Failed to confirm agreement. Please try again.";
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      
      setSnack({
        open: true,
        msg: errorMsg,
        sev: "error",
      });
    } finally {
      setRpaProcessing(false);
    }
  };

  /* ───────── CLAIM HANDLERS (from PayoutsInvoicing) ───────── */
  const handleClaimToBalance = async (payoutId) => {
    try {
      setClaimingPayoutId(payoutId);
      await client.creatorConnect.claimPayout(payoutId, 'balance');
      setSnack({
        open: true,
        msg: 'Payout successfully claimed to your balance!',
        sev: 'success'
      });
      fetchPayoutData();
    } catch (err) {
      setSnack({
        open: true,
        msg: err?.response?.data?.error || 'Failed to claim payout',
        sev: 'error'
      });
    } finally {
      setClaimingPayoutId(null);
    }
  };

  const handleClaimNet30 = async (payoutId) => {
    try {
      setClaimingPayoutId(payoutId);
      await client.creatorConnect.claimPayout(payoutId, 'balance', 'NET30');
      setSnack({
        open: true,
        msg: 'Payment claimed! You\'ll receive the full amount in 30-90 days with no fees.',
        sev: 'success'
      });
      fetchPayoutData();
    } catch (err) {
      setSnack({
        open: true,
        msg: err?.response?.data?.error || 'Failed to claim payout',
        sev: 'error'
      });
    } finally {
      setClaimingPayoutId(null);
    }
  };

  const handleApproveCampaignPayout = async (payoutId, paymentMethod) => {
    const creatorUsername = payout?.creator_id;
    if (!creatorUsername) {
      setSnack({ open: true, msg: 'Missing creator on payout.', sev: 'error' });
      return;
    }
    try {
      setClaimingPayoutId(payoutId);
      await client.creatorConnect.confirmPayment({
        username: creatorUsername,
        paymentMethod,
        payout_id: payoutId,
      });
      setSnack({
        open: true,
        msg: 'Payout timing saved. Waiting for admin to confirm.',
        sev: 'success',
      });
      fetchPayoutData();
    } catch (err) {
      setSnack({
        open: true,
        msg: err?.response?.data?.error || 'Failed to save payout timing',
        sev: 'error',
      });
    } finally {
      setClaimingPayoutId(null);
    }
  };

  const handleClaimWithWithdrawal = async (payoutId) => {
    try {
      setClaimingPayoutId(payoutId);
      const response = await client.creatorConnect.claimPayout(payoutId, 'withdraw', 'BLITZPAY');
      setSnack({
        open: true,
        msg: response?.message || 'Payout confirmed and withdrawal initiated!',
        sev: 'success'
      });
      fetchPayoutData();
    } catch (err) {
      const errorCode = err?.response?.data?.code;
      const onboardingUrl = err?.response?.data?.onboarding_url;

      if (errorCode === 'CONNECT_CAPABILITY_INACTIVE' && onboardingUrl) {
        setStripeVerifyDialog({ open: true, url: onboardingUrl });
      } else if (errorCode === 'NO_PAYMENT_METHOD') {
        setSnack({
          open: true,
          msg: 'Please add a payment method first',
          sev: 'warning'
        });
        setTimeout(() => {
          navigate('/creatorconnect/edit?tab=wallets');
        }, 2000);
      } else {
        setSnack({
          open: true,
          msg: err?.response?.data?.error || 'Failed to process withdrawal',
          sev: 'error'
        });
      }
    } finally {
      setClaimingPayoutId(null);
    }
  };

  /* ───────── Fetch Payout Data ───────── */
  const fetchPayoutData = async () => {
    try {
      const data = await client.payouts.get(payoutId);
      setPayout(data.payout);
      
      const creatorUsername = data.payout?.creator_id;
      
      if (!creatorUsername) {
        console.error('❌ No creator_username found in payout data');
        setSnack({
          open: true,
          msg: "Creator information is missing. Please contact support.",
          sev: "error"
        });
        setLoading(false);
        return;
      }
      
      console.log('💰 Payout data loaded for creator:', creatorUsername);
      
      const email = data.payout?.payout_email || data.payout?.creator_email;
      if (email) {
        setPaypalEmail(email);
      }
      
      await checkRpaStatus(creatorUsername);
    } catch (error) {
      replaceConsoleError('Error fetching payout:', error);
      setSnack({
        open: true,
        msg: "Unable to load payout. Please try again.",
        sev: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  /* ───────── Fetch Wallets ───────── */
  const fetchWallets = async () => {
    try {
      const response = await client.creatorConnect.getWallets();
      setWallets(response?.wallets || []);
    } catch (error) {
      console.error('Error fetching wallets:', error);
      setWallets([]);
    } finally {
      setWalletsLoading(false);
    }
  };

  /* ───────── Effects ───────── */
  useEffect(() => {
    fetchPayoutData();
    fetchWallets();
  }, [payoutId]);

  useEffect(() => {
    if (paypalEmail && !emailError && paypalEmail.includes('@')) {
      const delayDebounce = setTimeout(() => {
        const creatorUsername = payout?.creator_id;
        if (creatorUsername) {
          checkRpaStatus(creatorUsername);
        }
      }, 500);
      return () => clearTimeout(delayDebounce);
    }
  }, [paypalEmail, emailError, payout?.creator_id]);

  /* ───────── Email Input Handler ───────── */
  const handlePaypalEmailChange = (event) => {
    const value = event.target.value;
    setPaypalEmail(value);
    if (value.trim()) {
      const { error, warning } = validateEmail(value);
      setEmailError(error);
      setEmailWarning(warning);
    } else {
      setEmailError("");
      setEmailWarning("");
    }
  };

  const acceptSuggestion = () => {
    if (emailWarning && emailWarning.includes("Did you mean")) {
      const suggested = emailWarning.match(/Did you mean (.+?)\?/)?.[1];
      if (suggested) {
        setPaypalEmail(suggested);
        setEmailWarning("");
        setEmailError("");
      }
    }
  };

  /* ───────── Legacy Claim Button (for non-users) ───────── */
  const handleClaimOrLogin = () => {
    if (needsRpaAgreement) {
      setSnack({
        open: true,
        msg: "Please agree to the BlitzPay RPA Agreement first",
        sev: "warning"
      });
      return;
    }

    if (payout.has_creator_user) {
      navigate('/creatorconnect/login');
    } else {
      navigate(`/creatorconnect/${payout.creator_id}`);
    }
  };

  /* ───────── Status Display ───────── */
  const getStatusInfo = () => {
    const status = (payout?.status || '').toLowerCase();
    switch (status) {
      case "approved":
        return {
          color: "success",
          icon: <CheckIcon />,
          title: "Payment Confirmed!",
          message: "Your payment method has been confirmed and the payout is being processed.",
        };
      case "confirmed":
        return {
          color: "success",
          icon: <CheckIcon />,
          title: "Payment Confirmed!",
          message: "Your payment has been confirmed and is being processed.",
        };
      case "paid":
        return {
          color: "success",
          icon: <CheckIcon />,
          title: "Payment Completed!",
          message: "Your payment has been successfully sent to your account.",
        };
      case "cancelled":
        return {
          color: "error",
          icon: <BlockIcon />,
          title: "Payment Cancelled",
          message: "This payment has been cancelled and is no longer available.",
        };
      default:
        return {
          color: "warning",
          icon: <WarningIcon />,
          title: "Unknown Status",
          message: "The payout status is unclear. Please contact support.",
        };
    }
  };

  const formattedDate = (date) => new Date(date).toLocaleDateString();
  const getAmount = (amount) => amount || 0;
  const creatorDisplayName = payout?.creator_display_name || payout?.creator_id || "Creator";

  /* ───────── RPA Agreement Section ───────── */
  const RpaAgreementSection = () => {
    if (rpaStatus.loading) {
      return (
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Checking agreement status...</Typography>
          </Box>
        </Card>
      );
    }

    if (rpaStatus.hasSigned && rpaCheckboxChecked) {
      return (
        <Card variant="outlined" sx={{ p: 3, mb: 3, bgcolor: "success.50" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckIcon color="success" />
            <Box>
              <Typography variant="h6" color="success.main">
                Agreement Confirmed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You've agreed to the BlitzPay RPA Agreement
              </Typography>
            </Box>
          </Box>
        </Card>
      );
    }

    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3, border: 2, borderColor: "primary.main" }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DocumentIcon color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" color="primary">
                BlitzPay Repurchase Agreement Required
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please review and agree to receive instant payments
              </Typography>
            </Box>
            <Button
              onClick={() => setShowRpaDetails(!showRpaDetails)}
              endIcon={showRpaDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size="small"
            >
              {showRpaDetails ? "Hide" : "View"} Agreement
            </Button>
          </Box>

          <Collapse in={showRpaDetails}>
            <Paper variant="outlined" sx={{ p: 3, maxHeight: 400, overflow: 'auto', bgcolor: 'grey.50' }}>
              {rpaTemplate ? (
                <Box
                  dangerouslySetInnerHTML={{ __html: rpaTemplate.contract_body }}
                  sx={{
                    '& h1, & h2, & h3': { mt: 2, mb: 1 },
                    '& p': { mb: 1 },
                    '& ul, & ol': { pl: 3, mb: 2 },
                  }}
                />
              ) : (
                <Typography color="text.secondary">Loading agreement...</Typography>
              )}
            </Paper>
          </Collapse>

          <Divider />

          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setRpaCheckboxChecked(!rpaCheckboxChecked)}
          >
            <Checkbox
              checked={rpaCheckboxChecked}
              onChange={(e) => setRpaCheckboxChecked(e.target.checked)}
              color="primary"
            />
            <Typography variant="body2">
              I have read and agree to the BlitzPay Repurchase Agreement
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleRpaAgreement}
            disabled={rpaProcessing || !paypalEmail || !!emailError || !rpaCheckboxChecked}
            fullWidth
            sx={{ py: 1.5 }}
          >
            {rpaProcessing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                Processing Agreement...
              </>
            ) : (
              "I Agree - Continue"
            )}
          </Button>

          {(!paypalEmail || emailError) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                Please enter a valid PayPal email address first
              </Typography>
            </Alert>
          )}

          {!rpaCheckboxChecked && paypalEmail && !emailError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2">
                Please check the agreement checkbox above to proceed
              </Typography>
            </Alert>
          )}
        </Stack>
      </Card>
    );
  };

  /* ───────── LOADING STATE ───────── */
  if (loading) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <Card sx={{ p: 4, textAlign: "center", minWidth: 300 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Loading your payout...</Typography>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  /* ───────── ERROR STATE ───────── */
  if (!payout) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <Card sx={{ p: 4, textAlign: "center", maxWidth: 400 }}>
            <Typography variant="h5" color="error" gutterBottom>
              Payout Not Found
            </Typography>
            <Typography color="text.secondary">
              This payout may not exist or you don't have access to it.
            </Typography>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  const statusInfo = getStatusInfo();

  /* ───────── MAIN RENDER ───────── */
  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <HomeHeader />
        <Toolbar />
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", py: 4 }}>
          <Container maxWidth="md">
            <Stack spacing={3}>
              {/* Page Header */}
              <Card sx={{ p: 3 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Your Payment
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {payout.has_creator_user 
                    ? 'Claim your payment to your Blitz balance or withdraw directly to your payment method.'
                    : 'Create a Blitz account to claim this payment and access all features.'}
                </Typography>
              </Card>

              {/* ============================================ */}
              {/* MAIN CONTENT - DIFFERENT FOR USERS VS NON-USERS */}
              {/* ============================================ */}
              
              {!canClaimPayout ? (
                // Already claimed or cancelled - show status
                <Card elevation={3}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack spacing={3} alignItems="center">
                      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                        {statusInfo.icon}
                      </Box>
                      <Typography variant="h3" fontWeight="bold" gutterBottom color={`${statusInfo.color}.main`}>
                        {statusInfo.title}
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 2, opacity: 0.9 }}>
                        {statusInfo.message}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              ) : payout.has_creator_user ? (
                // User has account - show EnhancedPayoutCard
                <EnhancedPayoutCard
                  payout={payout}
                  onClaimToBalance={handleClaimToBalance}
                  onClaimWithWithdrawal={handleClaimWithWithdrawal}
                  onClaimNet30={handleClaimNet30}
                  onApproveCampaignPayout={payout.campaign_id ? handleApproveCampaignPayout : undefined}
                  wallets={wallets}
                  claimingPayoutId={claimingPayoutId}
                />
              ) : (
                // User doesn't have account - show legacy flow with RPA
                <Card elevation={3}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ textAlign: "center", mb: 4 }}>
                      <Typography variant="h5" sx={{ opacity: 0.9, mb: 2 }}>
                        Hello {creatorDisplayName}, you have{" "}
                        <strong>{money(getAmount(payout.amount))}</strong>.
                      </Typography>
                    </Box>

                    <Alert severity="success" sx={{ mb: 4 }}>
                      <Typography variant="body2">
                        <strong>Congrats!</strong> You got paid {money(getAmount(payout.amount))}.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        You can claim the money today for 5% or wait 30 days for the full amount to hit your Blitz account.
                      </Typography>
                    </Alert>

                    {/* PayPal Email Input */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        Enter PayPal Email to Check Contracts
                      </Typography>
                      <TextField
                        fullWidth
                        label="PayPal Email Address"
                        placeholder="your.email@paypal.com"
                        value={paypalEmail}
                        onChange={handlePaypalEmailChange}
                        error={!!emailError}
                        helperText={
                          emailError || "Enter your PayPal email to verify contract status"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      
                      {emailWarning && emailWarning.includes("Did you mean") && (
                        <Alert
                          severity="warning"
                          sx={{ mt: 1 }}
                          action={
                            <Button color="inherit" size="small" onClick={acceptSuggestion}>
                              Fix It
                            </Button>
                          }
                        >
                          <Typography variant="body2">{emailWarning}</Typography>
                        </Alert>
                      )}
                    </Box>

                    {/* RPA Section */}
                    <RpaAgreementSection />

                    {/* Payout Details */}
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        Payout Details
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body1">
                          Creator Name: {payout.creator_id}
                        </Typography>
                        <Typography variant="body1">
                          Amount: {money(getAmount(payout.amount))}
                        </Typography>
                        <Typography variant="body1">
                          Payout Date: {formattedDate(payout.payout_date)}
                        </Typography>
                        <Typography variant="body1">
                          Status: {payout.status}
                        </Typography>
                        {payout.notes && (
                          <Typography variant="body1">
                            Notes: {payout.notes}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Action Button */}
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4, pt: 2, borderTop: 1, borderColor: "divider" }}>
                      <Button
                        variant="contained"
                        onClick={handleClaimOrLogin}
                        disabled={needsRpaAgreement}
                        size="large"
                        startIcon={<LaunchIcon />}
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          minWidth: 250,
                          py: 1.5,
                        }}
                      >
                        Claim Your Free Blitz Account
                      </Button>
                    </Box>

                    {/* Instructions */}
                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                      <Typography variant="body1">
                        To accept this payment, claim your Blitz account and navigate to the Money page. 
                        Once there, you'll be able to select how fast you want to get paid!
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Help Section */}
              <Card sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Need help?</strong> Contact us at win@useblitz.co or chat with our CEO directly.
                </Typography>
              </Card>
            </Stack>
          </Container>
        </Box>
        <HomeFooter />
        
        <Snackbar
          open={snack.open}
          autoHideDuration={6000}
          onClose={() => setSnack({ ...snack, open: false })}
        >
          <Alert
            onClose={() => setSnack({ ...snack, open: false })}
            severity={snack.sev}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>

        {/* ── Stripe Identity Verification Dialog ── */}
        <Dialog
          open={stripeVerifyDialog.open}
          onClose={() => setStripeVerifyDialog({ open: false, url: null })}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, boxShadow: 6 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Verify Your Identity to Receive Payouts</DialogTitle>
          <DialogContent>
            <DialogContentText>
              To receive payouts, Stripe requires a one-time identity verification.
              This takes about 2 minutes and only needs to be done once — you'll
              never be asked again after completing it.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setStripeVerifyDialog({ open: false, url: null })} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                window.open(stripeVerifyDialog.url, '_blank');
                setStripeVerifyDialog({ open: false, url: null });
              }}
            >
              Complete Verification
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default PayoutPage;
